import React, { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useNavigate } from "react-router-dom";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import { Undo2, ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { debounce } from "lodash";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useTranslation } from "@/locales";
import { useTheme } from "@/contexts/ThemeContext";
import "./index.css";

import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

interface PdfViewH5Props {
  file: string;
  fileName?: string;
  initialPage?: number;
  onClose?: () => void;
  onPageChange?: (page: number) => void;
}

export const PdfViewH5: React.FC<PdfViewH5Props> = ({
  file,
  fileName = "PDF文档",
  initialPage = 1,
  onClose,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageInput, setPageInput] = useState<string>(initialPage.toString());
  const [rotation, setRotation] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [isPagesLoading, setIsPagesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManualZoom, setIsManualZoom] = useState(false);
  const [hasScrolledToInitial, setHasScrolledToInitial] = useState(false);

  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pageElementsRef = useRef<NodeListOf<Element> | null>(null);

  const isProgrammaticScroll = useRef(false);
  const lastScrollPosition = useRef(0);
  const scrollDirection = useRef<"up" | "down">("down");
  const scrollEndTimer = useRef<NodeJS.Timeout | null>(null);

  const pdfOptions = React.useMemo(() => ({
    cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  useEffect(() => {
    setNumPages(0);
    setCurrentPage(initialPage);
    setPageInput(initialPage.toString());
    setHasScrolledToInitial(false);
    setIsDocumentLoading(true);
    setIsPagesLoading(true);
    setError(null);
    setIsManualZoom(false);
    pdfDocRef.current = null;
  }, [file, initialPage]);

  const handleDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setNumPages(pdf.numPages);
    pdfDocRef.current = pdf;
    setIsDocumentLoading(false);
    computeAutoScale();
  };

  const handlePageRenderSuccess = () => {
    const allPagesLoaded = Array.from({ length: numPages }).every((_, i) => {
      return pageRefs.current[i]?.querySelector(".react-pdf__Page__canvas");
    });
    if (allPagesLoaded) {
      setIsPagesLoading(false);
    }
  };

  const handleDocumentLoadError = useCallback((error: Error) => {
    console.error("PDF加载失败:", error);
    setError(`无法加载PDF文件: ${error.message}`);
    setIsDocumentLoading(false);
    setIsPagesLoading(false);
    toast.error("PDF加载失败");
  }, []);

  const computeAutoScale = useCallback(async () => {
    if (isManualZoom || !pdfDocRef.current) return;
    try {
      const page = await pdfDocRef.current.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const containerEl = scrollContainerRef.current;
      if (!containerEl) return;
      const newScale = (containerEl.clientWidth - 16) / viewport.width;
      setScale(newScale);
    } catch (error) {
      console.error("自动缩放计算失败:", error);
    }
  }, [isManualZoom]);

  useEffect(() => {
    if (isManualZoom) return;
    const handleResize = debounce(() => computeAutoScale(), 300);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [computeAutoScale, isManualZoom]);

  const scrollToPage = (page: number) => {
    const pageElement = pageRefs.current[page - 1];
    if (pageElement) {
      isProgrammaticScroll.current = true;
      pageElement.scrollIntoView({ behavior: 'auto', block: 'start' });
      setCurrentPage(page);
      setPageInput(String(page));
      if (onPageChange) onPageChange(page);
      setTimeout(() => { isProgrammaticScroll.current = false; }, 500);
    }
  };

  useEffect(() => {
    if (!isPagesLoading && !hasScrolledToInitial) {
      const timer = setTimeout(() => {
        const targetPage = pageRefs.current[initialPage - 1];
        if (targetPage) {
          targetPage.scrollIntoView({ behavior: 'auto', block: 'start' });
          setCurrentPage(initialPage);
          setPageInput(String(initialPage));
        }
        setHasScrolledToInitial(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isPagesLoading, hasScrolledToInitial, initialPage, numPages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isDocumentLoading) return;

    const updatePageElements = () => {
      pageElementsRef.current = container.querySelectorAll(".pdf-h5-page-wrapper");
    };
    updatePageElements();

    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;

      const currentScrollPosition = container.scrollTop;
      scrollDirection.current = currentScrollPosition > lastScrollPosition.current ? "down" : "up";
      lastScrollPosition.current = currentScrollPosition;

      if (!pageElementsRef.current || pageElementsRef.current.length === 0) updatePageElements();

      let maxVisibleArea = 0;
      let currentVisiblePage = currentPage;
      pageElementsRef.current?.forEach((element, index) => {
        const pageElement = element as HTMLElement;
        const visibleRect = pageElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const visibleHeight = Math.max(0, Math.min(visibleRect.bottom, containerRect.bottom) - Math.max(visibleRect.top, containerRect.top));
        if (visibleHeight > maxVisibleArea) {
          maxVisibleArea = visibleHeight;
          currentVisiblePage = index + 1;
        }
      });

      if (currentVisiblePage !== currentPage) {
        setCurrentPage(currentVisiblePage);
        setPageInput(String(currentVisiblePage));
      }

      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
      scrollEndTimer.current = setTimeout(() => {
        if (onPageChange) onPageChange(currentPage);
      }, 500);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    };
  }, [isDocumentLoading, currentPage, onPageChange]);

  const handlePrevPage = useCallback(() => scrollToPage(currentPage - 1), [currentPage, scrollToPage]);
  const handleNextPage = useCallback(() => scrollToPage(currentPage + 1), [currentPage, scrollToPage]);

  const handleJumpToPage = () => {
    const target = parseInt(pageInput);
    if (!isNaN(target) && numPages > 0) {
      scrollToPage(Math.min(Math.max(target, 1), numPages));
    } else {
      setPageInput(String(currentPage));
    }
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleJumpToPage();
  };

  const handleZoomIn = useCallback(() => {
    setIsManualZoom(true);
    setScale(prev => Math.min(prev + 0.2, 3.0));
  }, []);

  const handleZoomOut = useCallback(() => {
    setIsManualZoom(true);
    setScale(prev => Math.max(prev - 0.2, 0.2));
  }, []);

  const containerClass = isDarkMode ? "pdf-h5-container pdf-h5-container-dark" : "pdf-h5-container";
  const headerClass = isDarkMode ? "pdf-h5-header pdf-h5-header-dark" : "pdf-h5-header";
  const titleClass = isDarkMode ? "pdf-h5-title pdf-h5-title-dark ml-2 line-clamp-2" : "pdf-h5-title ml-2 line-clamp-2";
  const contentClass = isDarkMode ? "pdf-h5-content pdf-h5-content-dark" : "pdf-h5-content";
  const toolbarClass = isDarkMode ? "pdf-h5-toolbar pdf-h5-toolbar-dark" : "pdf-h5-toolbar";
  const pageInputClass = isDarkMode
    ? "w-10 border-b bg-transparent text-center text-base font-semibold focus:outline-none border-[#334155] focus:border-[#38BDF8] text-white"
    : "w-10 border-b bg-transparent text-center text-base font-semibold focus:outline-none focus:border-blue-500";

  if (error) {
    return (
      <div className={containerClass}>
        <div className={headerClass}>
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <Undo2 className="h-6 w-6" />
          </Button>
          <span className={titleClass}>{fileName}</span>
        </div>
        <div className={isDarkMode ? "pdf-h5-error pdf-h5-error-dark" : "pdf-h5-error"}>
          <div className="error-icon">⚠️</div>
          <h3>加载失败</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={headerClass}>
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <Undo2 className="h-6 w-6" />
        </Button>
        <span className={titleClass}>{fileName}</span>
      </div>

      <div className={contentClass} ref={scrollContainerRef}>
        {isDocumentLoading && (
          <div className={isDarkMode ? "pdf-h5-loading pdf-h5-loading-dark" : "pdf-h5-loading"}>
            <div className="loading-spinner"></div>
            <p>加载中...</p>
          </div>
        )}
        <Document
          file={file}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          loading={
            <div className="space-y-4 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={`doc-skeleton-${i}`} className="w-full h-[400px] rounded-md" />
              ))}
            </div>
          }
          options={pdfOptions}
        >
          {Array.from(new Array(numPages), (el, index) => (
            <div
              key={`page_${index + 1}`}
              ref={(el) => (pageRefs.current[index] = el)}
              data-page-number={index + 1}
              className="pdf-h5-page-wrapper"
            >
              <div>
                <Page
                  pageNumber={index + 1}
                  rotate={rotation}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  onRenderSuccess={handlePageRenderSuccess}
                  loading={
                    <div className="pdf-h5-page-loading">
                      <div className="loading-spinner-small"></div>
                    </div>
                  }
                />
              </div>
            </div>
          ))}
        </Document>
      </div>

      <div className={toolbarClass}>
        <div className="pdf-h5-page-control">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="pdf-h5-page-info">
            <form onSubmit={handlePageInputSubmit} className="flex items-baseline">
              <input
                type="text"
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={handleJumpToPage}
                className={pageInputClass}
              />
              <span className="separator">/</span>
              <span className="total-pages">{numPages}</span>
            </form>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextPage}
            disabled={currentPage >= numPages}
            className="h-9 w-9"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        <div className="pdf-h5-zoom-control">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomOut}
            disabled={scale <= 0.2}
            className="h-9 w-9"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="pdf-h5-scale-info">{Math.round(scale * 100)}%</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleZoomIn}
            disabled={scale >= 3.0}
            className="h-9 w-9"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PdfViewH5;
